/**
 * Helpers for organisation.org_type_id as a single populated ref or an array of refs (multi-select).
 */

export function formatOrgTypesLabel(org_type_id) {
  if (org_type_id == null) return "N/A";
  if (Array.isArray(org_type_id)) {
    const parts = org_type_id
      .map((t) => (t && t.org_type != null ? String(t.org_type) : null))
      .filter(Boolean);
    return parts.length ? parts.join(", ") : "N/A";
  }
  if (typeof org_type_id === "object" && org_type_id.org_type != null) {
    return String(org_type_id.org_type);
  }
  return "N/A";
}

/** String ids for controlled multi-select */
export function orgTypeIdsForForm(org_type_id) {
  if (org_type_id == null) return [];
  if (Array.isArray(org_type_id)) {
    return org_type_id
      .map((t) => (t && t._id != null ? String(t._id) : t != null ? String(t) : null))
      .filter(Boolean);
  }
  if (typeof org_type_id === "object" && org_type_id._id != null) {
    return [String(org_type_id._id)];
  }
  return [];
}

/** Ids for filter matching (org has any of selected types) */
export function orgTypeIdList(org_type_id) {
  if (org_type_id == null) return [];
  if (Array.isArray(org_type_id)) {
    return org_type_id.map((t) => (t && t._id != null ? String(t._id) : String(t))).filter(Boolean);
  }
  const id = org_type_id._id != null ? String(org_type_id._id) : String(org_type_id);
  return id && id !== "undefined" ? [id] : [];
}

export function orgHasOrgTypeId(org_type_id, selectedId) {
  if (selectedId == null) return false;
  return orgTypeIdList(org_type_id).includes(String(selectedId));
}

/** Match populated type label (e.g. filter committees where one type is "Committee") */
export function orgHasOrgTypeLabel(org_type_id, label) {
  if (label == null) return false;
  const want = String(label).toLowerCase();
  const types = Array.isArray(org_type_id) ? org_type_id : org_type_id ? [org_type_id] : [];
  return types.some((t) => String(t?.org_type || "").toLowerCase() === want);
}

/** Match sidebar key from buildOrgTypeBuckets: either Mongo id string or `type:Label` */
export function orgMatchesOrgTypeFilter(org_type_id, filterKey) {
  if (filterKey == null) return false;
  if (String(filterKey).startsWith("type:")) {
    return orgHasOrgTypeLabel(org_type_id, String(filterKey).slice("type:".length));
  }
  return orgHasOrgTypeId(org_type_id, filterKey);
}

/** Lowercase string of all org type labels (same join as table display) for substring filters. */
export function orgTypeFilterHaystack(org_type_id) {
  const types = Array.isArray(org_type_id) ? org_type_id : org_type_id ? [org_type_id] : [];
  const parts = types.map((t) => (t && t.org_type != null ? String(t.org_type) : "")).filter(Boolean);
  return parts.length ? parts.join(", ").toLowerCase() : "";
}

/**
 * Sidebar id / `type:Label` match, plus case-insensitive substring match like:
 *   filters.orgType.some((type) => itemOrgTypeLower.includes(type.toLowerCase()))
 * (Haystack is built from populated org_type labels, not a flat `item.orgType` field.)
 */
export function orgMatchesOrgTypeFilterFlexible(org_type_id, filterKey) {
  if (filterKey == null || filterKey === "") return false;
  if (orgMatchesOrgTypeFilter(org_type_id, filterKey)) return true;
  const haystack = orgTypeFilterHaystack(org_type_id);
  if (!haystack) return false;
  let needle = String(filterKey).toLowerCase();
  if (needle.startsWith("type:")) needle = needle.slice("type:".length);
  if (!needle) return false;
  // 24-hex ids: exact id match only (handled above); also allow needle as substring of haystack if id ever appeared in labels (no-op)
  if (/^[a-f0-9]{24}$/i.test(needle)) return false;
  // Row haystack contains filter text (partial), e.g. "delivery partners" includes "delivery"
  if (haystack.includes(needle)) return true;
  // Filter text contains a whole type label (e.g. long sidebar label vs short stored name)
  const types = Array.isArray(org_type_id) ? org_type_id : org_type_id ? [org_type_id] : [];
  return types.some((t) => {
    const lab = String(t?.org_type || "").toLowerCase();
    return lab && (needle.includes(lab) || lab.includes(needle));
  });
}
